/**
 * WebhookHelpDialog - Guia explicativo do Webhook de Integração
 *
 * Modal de ajuda com:
 *  - Visão Geral (o que é, quando usar, modos)
 *  - Passo a passo de configuração
 *  - 5 exemplos práticos copiáveis (Conversa e Negócios)
 *
 * Sem gradientes/efeitos "IA" — segue padrão visual do sistema.
 *
 * @version 1.0.0
 * @date 2026-05-31
 */

import React, { useState } from 'react';
import {
  BookOpen,
  Copy,
  Check,
  MessageSquare,
  Briefcase,
  ArrowRight,
  Shield,
  Zap,
  AlertCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from 'sonner';

interface WebhookHelpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// Bloco de código com botão "Copiar"
// ---------------------------------------------------------------------------
const CodeBlock: React.FC<{ code: string; label?: string }> = ({ code, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Copiado para a área de transferência');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Não foi possível copiar');
    }
  };

  return (
    <div className="relative group min-w-0 w-full">
      {label && (
        <div className="text-[11px] font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">
          {label}
        </div>
      )}
      <pre className="bg-muted/50 border border-border rounded-md p-3 font-mono text-xs overflow-x-auto leading-relaxed max-w-full whitespace-pre">
        {code}
      </pre>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute top-1 right-1 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={handleCopy}
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Definição dos 5 exemplos
// ---------------------------------------------------------------------------
type ExampleMode = 'conversa' | 'negocios';

interface UseCaseExample {
  id: string;
  mode: ExampleMode;
  title: string;
  scenario: string;
  config: { label: string; value: string }[];
  variables?: { name: string; path: string }[];
  payload: string;
  curl: string;
  result: string;
}

const EXAMPLES: UseCaseExample[] = [
  // -------------------------------------------------------------------------
  // 1. Conversa — Formulário do site
  // -------------------------------------------------------------------------
  {
    id: 'site-form',
    mode: 'conversa',
    title: 'Formulário do site → boas-vindas no WhatsApp',
    scenario:
      'Quando alguém preenche o formulário de contato do site, dispara um fluxo de boas-vindas no WhatsApp do lead.',
    config: [
      { label: 'Modo', value: 'Conversa' },
      { label: 'Inbox', value: 'WhatsApp Principal' },
      { label: 'Telefone (mapeamento)', value: 'telefone' },
      { label: 'Nome (mapeamento)', value: 'nome' },
      { label: 'Email (mapeamento)', value: 'email' },
    ],
    payload: `{
  "telefone": "5511999998888",
  "nome": "Maria Souza",
  "email": "maria@exemplo.com"
}`,
    curl: `curl -X POST https://SEU_DOMINIO/functions/v1/flow-webhook/SEU_TOKEN \\
  -H "Content-Type: application/json" \\
  -d '{"telefone":"5511999998888","nome":"Maria Souza","email":"maria@exemplo.com"}'`,
    result:
      'Cria/reabre a conversa no inbox selecionado e inicia o fluxo a partir do gatilho Webhook.',
  },

  // -------------------------------------------------------------------------
  // 2. Conversa — Carrinho abandonado
  // -------------------------------------------------------------------------
  {
    id: 'abandoned-cart',
    mode: 'conversa',
    title: 'Carrinho abandonado (e-commerce)',
    scenario:
      'A plataforma de e-commerce dispara o webhook 1h após o carrinho ser abandonado, com produto e link de checkout para o fluxo de recuperação.',
    config: [
      { label: 'Modo', value: 'Conversa' },
      { label: 'Inbox', value: 'WhatsApp Vendas' },
      { label: 'Telefone (mapeamento)', value: 'cliente.telefone' },
      { label: 'Nome (mapeamento)', value: 'cliente.nome' },
    ],
    variables: [
      { name: 'produto', path: 'carrinho.produto_principal' },
      { name: 'valor', path: 'carrinho.total' },
      { name: 'link_checkout', path: 'carrinho.url_retomar' },
    ],
    payload: `{
  "cliente": {
    "nome": "João Lima",
    "telefone": "5521988887777"
  },
  "carrinho": {
    "produto_principal": "Tênis Runner Pro",
    "total": "R$ 459,90",
    "url_retomar": "https://loja.com/checkout/abc123"
  }
}`,
    curl: `curl -X POST https://SEU_DOMINIO/functions/v1/flow-webhook/SEU_TOKEN \\
  -H "Content-Type: application/json" \\
  -d @carrinho.json`,
    result:
      'No fluxo, use {{produto}}, {{valor}} e {{link_checkout}} nos nodes de mensagem para personalizar a recuperação.',
  },

  // -------------------------------------------------------------------------
  // 3. Negócios — Lead de anúncios (Zapier)
  // -------------------------------------------------------------------------
  {
    id: 'ads-lead',
    mode: 'negocios',
    title: 'Lead de anúncios (Meta/Google Ads via Zapier)',
    scenario:
      'O Zapier recebe leads de campanhas pagas e envia para o webhook, criando um deal no pipeline Inbound com origem registrada.',
    config: [
      { label: 'Modo', value: 'Negócios' },
      { label: 'Workspace CRM', value: 'Comercial' },
      { label: 'Pipeline', value: 'Inbound' },
      { label: 'Fase Inicial', value: 'Novos Leads' },
      { label: 'Origem do Negócio', value: 'Facebook Ads' },
      { label: 'Telefone', value: 'phone' },
      { label: 'Nome', value: 'full_name' },
      { label: 'Email', value: 'email' },
      { label: 'Título do Negócio', value: 'campaign_name' },
    ],
    payload: `{
  "full_name": "Ana Pereira",
  "phone": "5531977776666",
  "email": "ana@empresa.com",
  "campaign_name": "Promo Black Friday - Plano Pro"
}`,
    curl: `curl -X POST https://SEU_DOMINIO/functions/v1/flow-webhook/SEU_TOKEN \\
  -H "Content-Type: application/json" \\
  -d '{"full_name":"Ana Pereira","phone":"5531977776666","email":"ana@empresa.com","campaign_name":"Promo Black Friday - Plano Pro"}'`,
    result:
      'Cria contato e deal na fase "Novos Leads" com origem "Facebook Ads" e dispara o fluxo de qualificação.',
  },

  // -------------------------------------------------------------------------
  // 4. Negócios — Contrato assinado (D4Sign/ZapSign)
  // -------------------------------------------------------------------------
  {
    id: 'contract-signed',
    mode: 'negocios',
    title: 'Contrato assinado (D4Sign / ZapSign)',
    scenario:
      'Quando o signatário conclui a assinatura, o webhook move o negócio para "Contrato assinado" e envia o link do documento.',
    config: [
      { label: 'Modo', value: 'Negócios' },
      { label: 'Workspace CRM', value: 'Comercial' },
      { label: 'Pipeline', value: 'Pós-Venda' },
      { label: 'Fase Inicial', value: 'Contrato assinado' },
      { label: 'Telefone', value: 'signatario.telefone' },
      { label: 'Nome', value: 'signatario.nome' },
      { label: 'Email', value: 'signatario.email' },
    ],
    variables: [
      { name: 'link_contrato', path: 'documento.url' },
      { name: 'numero_contrato', path: 'documento.numero' },
    ],
    payload: `{
  "signatario": {
    "nome": "Carlos Mendes",
    "telefone": "5547966665555",
    "email": "carlos@cliente.com"
  },
  "documento": {
    "numero": "CT-2026-00123",
    "url": "https://d4sign.com/doc/abc123"
  }
}`,
    curl: `curl -X POST https://SEU_DOMINIO/functions/v1/flow-webhook/SEU_TOKEN \\
  -H "Content-Type: application/json" \\
  -d @assinatura.json`,
    result:
      'Cria deal já na fase "Contrato assinado". Use {{link_contrato}} e {{numero_contrato}} no fluxo de boas-vindas pós-assinatura.',
  },

  // -------------------------------------------------------------------------
  // 5. Negócios — Pagamento confirmado
  // -------------------------------------------------------------------------
  {
    id: 'payment-confirmed',
    mode: 'negocios',
    title: 'Pagamento confirmado (gateway externo)',
    scenario:
      'O gateway de pagamento dispara o webhook ao confirmar pagamento, criando o deal já marcado como pago e com tags de segmentação.',
    config: [
      { label: 'Modo', value: 'Negócios' },
      { label: 'Workspace CRM', value: 'Financeiro' },
      { label: 'Pipeline', value: 'Vendas' },
      { label: 'Fase Inicial', value: 'Pago' },
      { label: 'Telefone', value: 'customer.phone' },
      { label: 'Nome', value: 'customer.name' },
      { label: 'Email', value: 'customer.email' },
      { label: 'Título do Negócio', value: 'order.description' },
      { label: 'Tags', value: 'order.tags' },
    ],
    variables: [
      { name: 'valor_pago', path: 'order.amount' },
      { name: 'metodo_pagamento', path: 'payment.method' },
      { name: 'id_transacao', path: 'payment.id' },
    ],
    payload: `{
  "customer": {
    "name": "Patrícia Rocha",
    "phone": "5511955554444",
    "email": "patricia@cliente.com"
  },
  "order": {
    "description": "Plano Anual Pro",
    "amount": "R$ 2.400,00",
    "tags": ["cliente-novo", "pago", "plano-anual"]
  },
  "payment": {
    "id": "pay_8f2a1b",
    "method": "PIX"
  }
}`,
    curl: `curl -X POST https://SEU_DOMINIO/functions/v1/flow-webhook/SEU_TOKEN \\
  -H "Content-Type: application/json" \\
  -d @pagamento.json`,
    result:
      'Cria o deal na fase "Pago" com tags aplicadas. Use {{valor_pago}}, {{metodo_pagamento}} e {{id_transacao}} no fluxo de confirmação.',
  },
];

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
export const WebhookHelpDialog: React.FC<WebhookHelpDialogProps> = ({
  open,
  onOpenChange,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[85vh] max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
        {/* Header compacto */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold">
            <BookOpen className="h-4 w-4 text-muted-foreground" />
            Guia de Webhooks de Integração
          </DialogTitle>
          <DialogDescription className="text-sm">
            Aprenda a configurar webhooks para receber dados externos e disparar fluxos
            automaticamente.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="overview" className="flex-1 min-h-0 overflow-hidden flex flex-col">
          <div className="px-6 pt-4 shrink-0">
            <TabsList>
              <TabsTrigger value="overview">Visão Geral</TabsTrigger>
              <TabsTrigger value="steps">Passo a passo</TabsTrigger>
              <TabsTrigger value="examples">Exemplos</TabsTrigger>
            </TabsList>
          </div>

          {/* ---------------- Visão Geral ---------------- */}
          <TabsContent value="overview" className="flex-1 min-h-0 overflow-hidden mt-0 data-[state=inactive]:hidden">
            <ScrollArea className="h-full w-full">

              <div className="px-6 py-5 space-y-5 text-sm">
                <section className="space-y-2">
                  <h3 className="font-semibold text-foreground">O que é</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Um webhook é uma URL única do seu fluxo que aceita requisições HTTP POST
                    com JSON. Ao receber uma requisição válida, o fluxo é disparado usando os
                    dados enviados — sem necessidade de polling ou integração nativa.
                  </p>
                </section>

                <section className="space-y-3">
                  <h3 className="font-semibold text-foreground">Modos de operação</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="border border-border rounded-md p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">Conversa</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Cria ou reabre uma conversa no inbox selecionado e inicia o fluxo.
                        Ideal para mensagens, atendimentos e notificações.
                      </p>
                    </div>
                    <div className="border border-border rounded-md p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">Negócios</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Cria um deal no CRM (workspace + pipeline + fase definidos) e inicia
                        o fluxo. Ideal para leads, contratos e pagamentos.
                      </p>
                    </div>
                  </div>
                </section>

                <section className="space-y-2">
                  <h3 className="font-semibold text-foreground">Como funciona</h3>
                  <ol className="space-y-2 text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">1</span>
                      <span>Sistema externo envia POST com JSON para a URL do webhook.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">2</span>
                      <span>Token na URL valida e identifica o fluxo de destino.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">3</span>
                      <span>Campos mapeados são extraídos do payload (dot notation aceita).</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">4</span>
                      <span>Variáveis ficam disponíveis no fluxo via <code className="font-mono text-xs bg-muted px-1 rounded">{'{{nome_variavel}}'}</code>.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">5</span>
                      <span>Fluxo executa do node Webhook em diante.</span>
                    </li>
                  </ol>
                </section>

                <section className="space-y-2">
                  <div className="flex items-start gap-2 p-3 border border-border rounded-md bg-muted/30">
                    <Shield className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="text-xs text-muted-foreground leading-relaxed">
                      <strong className="text-foreground">Segurança:</strong> o token na URL é
                      sua chave de autenticação. Trate-o como secreto e regenere imediatamente
                      se for exposto.
                    </div>
                  </div>
                  <div className="flex items-start gap-2 p-3 border border-border rounded-md bg-muted/30">
                    <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="text-xs text-muted-foreground leading-relaxed">
                      <strong className="text-foreground">Fluxo precisa estar Ativo:</strong>{' '}
                      webhooks só processam requisições quando o flow está publicado.
                      Rascunhos ignoram requisições.
                    </div>
                  </div>
                </section>
              </div>
            </ScrollArea>
          </TabsContent>

          {/* ---------------- Passo a passo ---------------- */}
          <TabsContent value="steps" className="flex-1 min-h-0 overflow-hidden mt-0 data-[state=inactive]:hidden">
            <ScrollArea className="h-full w-full">

              <div className="px-6 py-5 space-y-4 text-sm">
                {[
                  {
                    title: 'Escolha o modo',
                    desc: 'Conversa (cria/reabre conversa em uma inbox) ou Negócios (cria deal no CRM).',
                  },
                  {
                    title: 'Selecione o destino',
                    desc: 'Conversa → escolha a inbox. Negócios → escolha workspace + pipeline + fase inicial (e origem, opcional).',
                  },
                  {
                    title: 'Mapeie os campos do payload',
                    desc: 'Informe o caminho do campo no JSON. Aceita dot notation para campos aninhados (ex: signatario.telefone).',
                  },
                  {
                    title: 'Crie variáveis customizadas',
                    desc: 'Mapeie qualquer campo extra para uma variável. Use no fluxo com {{nome_variavel}}.',
                  },
                  {
                    title: 'Copie URL e token',
                    desc: 'Use a URL gerada na sua integração externa (Zapier, n8n, gateway, formulário, etc.).',
                  },
                  {
                    title: 'Publique o fluxo e teste',
                    desc: 'Use a aba "Testar Webhook" com um payload de exemplo. Verifique o resultado em "Logs".',
                  },
                ].map((step, idx) => (
                  <div
                    key={step.title}
                    className="flex gap-3 border border-border rounded-md p-4"
                  >
                    <div className="shrink-0 w-7 h-7 rounded-full bg-muted flex items-center justify-center font-mono text-xs font-semibold">
                      {idx + 1}
                    </div>
                    <div className="space-y-1">
                      <div className="font-medium">{step.title}</div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                ))}

                <div className="flex items-start gap-2 p-3 border border-border rounded-md bg-muted/30 mt-2">
                  <Zap className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="text-xs text-muted-foreground leading-relaxed">
                    <strong className="text-foreground">Dica:</strong> sempre comece com o
                    payload real do sistema externo em mãos. Configure o mapeamento baseado
                    nele e teste antes de publicar.
                  </div>
                </div>
              </div>
            </ScrollArea>
          </TabsContent>

          {/* ---------------- Exemplos ---------------- */}
          <TabsContent value="examples" className="flex-1 min-h-0 overflow-hidden mt-0 data-[state=inactive]:hidden">
            {/* Força o viewport interno do Radix ScrollArea a usar block + width total,
                evitando que conteúdo largo (ex.: cURL em linha única) empurre o modal. */}
            <ScrollArea className="h-full w-full [&>[data-radix-scroll-area-viewport]>div]:!block [&>[data-radix-scroll-area-viewport]>div]:!w-full">

              <div className="px-6 py-5 min-w-0">
                <Accordion type="single" collapsible className="space-y-2">
                  {EXAMPLES.map((ex, idx) => (
                    <AccordionItem
                      key={ex.id}
                      value={ex.id}
                      className="border border-border rounded-md px-4 min-w-0 overflow-hidden"
                    >
                      <AccordionTrigger className="hover:no-underline py-3">
                        <div className="flex items-center gap-3 text-left flex-1">
                          <span className="font-mono text-xs text-muted-foreground shrink-0 w-5">
                            {idx + 1}
                          </span>
                          <Badge
                            variant={ex.mode === 'conversa' ? 'secondary' : 'outline'}
                            className="shrink-0 gap-1"
                          >
                            {ex.mode === 'conversa' ? (
                              <MessageSquare className="h-3 w-3" />
                            ) : (
                              <Briefcase className="h-3 w-3" />
                            )}
                            {ex.mode === 'conversa' ? 'Conversa' : 'Negócios'}
                          </Badge>
                          <span className="text-sm font-medium">{ex.title}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-2 pb-4 space-y-4 min-w-0">
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {ex.scenario}
                        </p>

                        <div className="space-y-2">
                          <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                            Configuração recomendada
                          </div>
                          <div className="border border-border rounded-md divide-y divide-border text-xs">
                            {ex.config.map((c) => (
                              <div
                                key={c.label}
                                className="flex items-center justify-between px-3 py-2 gap-3"
                              >
                                <span className="text-muted-foreground">{c.label}</span>
                                <span className="font-mono text-foreground text-right">
                                  {c.value}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {ex.variables && ex.variables.length > 0 && (
                          <div className="space-y-2">
                            <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                              Variáveis customizadas
                            </div>
                            <div className="border border-border rounded-md divide-y divide-border text-xs">
                              {ex.variables.map((v) => (
                                <div
                                  key={v.name}
                                  className="flex items-center justify-between px-3 py-2 gap-3 font-mono"
                                >
                                  <span>{'{{' + v.name + '}}'}</span>
                                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-muted-foreground text-right flex-1">
                                    {v.path}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <CodeBlock code={ex.payload} label="Payload JSON" />
                        <CodeBlock code={ex.curl} label="Exemplo cURL" />

                        <div className="flex items-start gap-2 p-3 border border-border rounded-md bg-muted/30">
                          <Check className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                          <div className="text-xs text-muted-foreground leading-relaxed">
                            <strong className="text-foreground">Resultado:</strong>{' '}
                            {ex.result}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default WebhookHelpDialog;
